import moment from "moment";
import { cloneDeep } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import {
  ListFieldsRecordType,
  emailsSender,
  paramsListName,
  siteUrl,
} from "src/api/config";
import { emailQueueListName } from "src/api/config/email.config";
import {
  notificationIdsListName,
  notificationTests,
  notificationTestsAnswers,
  notificationTestsQuestion,
  notificationsListName,
} from "src/api/config/notifications.config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import Loading from "src/components/Loading";
import TGButton from "src/components/ui/TGButton";
import {
  NotificationIcon,
  NotificationSmallIcon,
} from "src/components/ui-icons/card";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getNotificationsInfo } from "src/modules/acadia/notifications/actions";
import { addAnalysis } from "src/modules/acadia/constants";
import { addItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { selectCurrentUser } from "src/modules/users/selector";
import { emailAPI, getParamsByKey } from "src/utils/api.utils";
import { FlexCol } from "src/utils/flexLayouts";
import { removeInvisibleChar } from "src/utils/string.utils";
import { ITesting } from "../components/Tests/types";
import ChooseTypeOfNotifyMobile, {
  NotifyTypeMobile,
} from "./ChooseTypeOfNotify.mobile";
import SelectCommentMobile from "./SelectComment.mobile";
import SelectDocumentsMobile, { NotifyMyDocumentsType } from "./SelectDocuments.mobile";
import SelectEmployeesMobile, {
  NotifyMyEmployeesType,
} from "./SelectEmployees.mobile";
import SelectReviewDateMobile from "./SelectReviewDate.mobile";
import SelectTestsMobile from "./SelectTests.mobile";
import { useStatusContext } from "./index";

const { sendEmail, createEmailBodyByTemplate } = emailAPI();

const mobileDrawerFooterButtonClassName =
  "h-[46px] w-full justify-center text-[14px]";

type SubmitType = {
  documents: Map<number, NotifyMyDocumentsType>;
  reviewDate: Date;
  comment: string;
  employees: Map<number, NotifyMyEmployeesType>;
  test: ITesting;
};

export default function NotificationSendButtonMobile({
  isIconOnly = false,
  changeActive,
  read,
  title = "Отправить уведомление",
  zIndex = 50,
  initialSubmit = {
    documents: new Map(),
    reviewDate: moment(new Date()).add(30, "day").toDate(),
    comment: "",
    employees: new Map(),
    test: {
      Questions: [],
      IsQuestionFormOpen: false,
      MinCorrectAnswersCount: 1,
    },
  },
}: {
  isIconOnly?: boolean;
  changeActive(active: "get" | "send"): void;
  read?: Record<any, boolean>;
  title?: string;
  zIndex?: number;
  initialSubmit?: SubmitType;
}) {
  const [submitState, setSubmitState] = useState<SubmitType>(
    cloneDeep(initialSubmit),
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const { changeStatus } = useStatusContext();
  const { resetFilters } = useFiltersContext();
  const [open, setOpen] = useState(false);
  const [openDrawerAudit, setOpenDrawerAudit] = useState(false);
  const [openDrawerOWD, setOpenDrawerOWD] = useState(false);
  const [openSubmitNotificationModal, setOpenSubmitNotificationModal] =
    useState(false);
  const [typeOfNotify, setTypeOfNotify] = useState<NotifyTypeMobile>("audit");

  useEffect(() => {
    if (!openDrawerAudit) {
      localStorage.setItem("questions", "");
      localStorage.setItem("minCorrectAnswersCount", "1");
    }
  }, [openDrawerAudit]);

  const clearSubmit = () => {
    setSubmitState(cloneDeep(initialSubmit));
    resetFilters();
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const closeAllModals = () => {
    setOpen(false);
    setOpenDrawerAudit(false);
    setOpenDrawerOWD(false);
    setOpenSubmitNotificationModal(false);
    clearSubmit();
  };

  const isButtonDisabledMemoized = useMemo(() => {
    return (
      !!submitState.documents.size &&
      !!submitState.employees.size &&
      !!submitState.reviewDate
    );
  }, [submitState, submitState.documents]);

  async function handleAuditSubmit() {
    try {
      setSubmitLoading(true);

      const questions = submitState.test.Questions;
      const questionsID = [];
      let answersID = [];
      const minCorrectAnswerCount = submitState.test.MinCorrectAnswersCount;

      const notificationID = (await addItemRequest({
        listName: notificationIdsListName,
        body: { Title: "Ознакомление" },
        updateReducer: false,
      })) as number;

      if (questions.length) {
        for (const question of questions) {
          for (const answer of question.Answers) {
            const ID = await addItemRequest({
              listName: notificationTestsAnswers,
              body: { ...answer, Title: answer.Description },
              updateReducer: false,
            });
            answersID.push(ID);
          }

          const idQuestion = await addItemRequest({
            listName: notificationTestsQuestion,
            body: {
              ...question,
              Title: question.Description,
              Answers: answersID,
            },
            updateReducer: false,
          });

          questionsID.push(idQuestion);
          answersID.length = 0;
        }
      }

      const testID: unknown = questions.length
        ? await addItemRequest({
            listName: notificationTests,
            body: {
              Title: `Тест № ${notificationID}`,
              Questions: questionsID,
              MinCorrectAnswerCount: minCorrectAnswerCount,
            },
            updateReducer: false,
          })
        : null;

      const notificationPromises = [];
      const emailPromises = [];

      for (const [, emp] of submitState.employees) {
        const notifyMap = new Map();

        for (const [, doc] of submitState.documents) {
          const submitObject: Partial<
            Record<keyof ListFieldsRecordType[typeof notificationsListName], any>
          > = {
            Title: "Уведомление " + notificationID,
            NotificationID: notificationID,
            Employee: emp.user.email.toLowerCase(),
            EmployeeEmail: emp.user.email.toLowerCase(),
            Document: doc.id,
            DocumentCode: doc.code.code,
            ReviewDate: submitState.reviewDate,
            AuthorComment: submitState.comment,
            NotificationType: 5,
            AuthorEmail: currentUser.email.toLowerCase(),
          };

          if (testID) {
            submitObject.Test = testID as number;
            submitObject.NotificationType = 4;
          }

          const notificationPromise = addItemRequest({
            listName: notificationsListName,
            body: submitObject,
            updateReducer: false,
          }).then((id: unknown) => {
            const notificationsListID = id as number;
            if (notificationsListID > 0) {
              if (notifyMap.has(doc)) {
                const arrayBody = notifyMap.get(doc);
                notifyMap.set(doc, [...arrayBody, notificationsListID]);
              } else {
                notifyMap.set(doc, [notificationsListID]);
              }
            }
          });

          notificationPromises.push(notificationPromise);
        }

        await Promise.all(notificationPromises);

        if (emailsSender) {
          const tempEmailBodyObject = {
            subject: `Мои документы | Ознакомление с документами`,
            user: {
              name:
                emp.user.name + ` (${emp.user.personalNumber || "Нет номера"})`,
              email: emp.user.email,
            },
            document: {
              title: `Прочитайте следующий (-е) документ (-ы)${
                questions.length ? ", пройдите тестирование" : ""
              } и подтвердите факт ознакомления с документом (-ами)`,
              lists: [...notifyMap.entries()].map(([doc, id]) => ({
                link: `${siteUrl}/dashboard/1/notification?notificationCard=${id}`,
                title: doc.code.name,
              })),
            },
            periodDate: {
              title: "Срок ознакомления",
              date: submitState.reviewDate,
            },
            header: `Supply Portal -> Мои документы | Ознакомление с документами`,
          };

          const emailBody = createEmailBodyByTemplate(tempEmailBodyObject);

          const { items } = await getItemsRequest({
            listName: paramsListName,
            filters: { Key: "audit" },
          });

          const stringWithoutInvisibleChar = removeInvisibleChar(items[0].Value);
          const emailRequestBody: Partial<
            ListFieldsRecordType[typeof emailQueueListName]
          > = {
            Body: emailBody,
            DateToBeSentAt: new Date(),
            Title: `Ознакомление с документами`,
            Subject: `Мои документы | Ознакомление с документами`,
            NotificationID: notificationID,
            Recepients: emp.user.email.toLowerCase(),
            RepeatAt: 0,
            RepeatTimes: 0,
          };

          emailPromises.push(
            sendEmail(emailRequestBody).then(async () => {
              if (items[0] && items[0].Value) {
                if (
                  !moment(submitState.reviewDate).isSame(
                    emailRequestBody.DateToBeSentAt,
                    "day",
                  )
                ) {
                  const expiredEmailBody = createEmailBodyByTemplate({
                    ...tempEmailBodyObject,
                    periodDate: {
                      ...tempEmailBodyObject.periodDate,
                      expired: true,
                    },
                  });

                  emailRequestBody.Body = expiredEmailBody;
                  emailRequestBody.DateToBeSentAt = submitState.reviewDate;
                  emailRequestBody.RepeatAt = +stringWithoutInvisibleChar;
                  emailRequestBody.RepeatTimes = 100000;

                  await sendEmail(emailRequestBody);
                }
              }
            }),
          );
        }
      }

      await Promise.all(emailPromises);

      const firstDoc = [...submitState.documents.values()][0];
      addAnalysis(
        currentUser,
        firstDoc.id,
        firstDoc.code.code,
        "isActive",
        "OWD",
      );

      setOpenSubmitNotificationModal(true);
      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        }),
      );
      changeActive("send");
    } catch (error) {
      console.log("error => handleAuditSubmit", error);
      throw new Error("Function not implemented.");
    } finally {
      changeStatus(Date.now());
      setSubmitLoading(false);
    }
  }

  async function handleOWDSubmit() {
    try {
      setSubmitLoading(true);

      const notificationID = (await addItemRequest({
        listName: notificationIdsListName,
        body: { Title: "OWD" },
        updateReducer: false,
      })) as number;

      const notificationsPromises = [];
      const emailPromises = [];
      const notifyMaps = new Map<string, Map<NotifyMyDocumentsType, number[]>>();

      for (const [, emp] of submitState.employees) {
        const notifyMap = new Map<NotifyMyDocumentsType, number[]>();
        notifyMaps.set(emp.user.email.toLowerCase(), notifyMap);

        for (const [, doc] of submitState.documents) {
          const submitObject = {
            Title: "OWD " + notificationID,
            NotificationID: notificationID,
            Employee: emp.user.email.toLowerCase(),
            EmployeeEmail: emp.user.email.toLowerCase(),
            Document: doc.id,
            DocumentCode: doc.code.code,
            ReviewDate: submitState.reviewDate,
            AuthorComment: submitState.comment,
            NotificationType: 6,
            AuthorEmail: currentUser.email.toLowerCase(),
          };

          const notificationPromise = addItemRequest({
            listName: notificationsListName,
            body: submitObject,
            updateReducer: false,
          }).then((id: unknown) => {
            const notificationsListID = id as number;

            if (notificationsListID > 0) {
              if (notifyMap.has(doc)) {
                const arrayBody = notifyMap.get(doc) || [];
                notifyMap.set(doc, [...arrayBody, notificationsListID]);
              } else {
                notifyMap.set(doc, [notificationsListID]);
              }
            }
          });

          notificationsPromises.push(notificationPromise);
        }
      }

      await Promise.all(notificationsPromises);

      if (emailsSender) {
        for (const [, emp] of submitState.employees) {
          const notifyMap = notifyMaps.get(emp.user.email.toLowerCase());

          if (notifyMap) {
            const tempEmailBodyObject = {
              subject: `Мои Документы | Проведение OWD`,
              user: {
                name:
                  emp.user.name +
                  ` (${emp.user.personalNumber || "Нет номера"})`,
                email: emp.user.email,
              },
              document: {
                title: `Проведите OWD по следующему (-им) документу (-ам)`,
                lists: [...notifyMap.entries()].map(([doc, ids]) => ({
                  link: `${siteUrl}/dashboard/1/notification?notificationCard=${ids.join(",")}`,
                  title: doc.code.name,
                })),
              },
              periodDate: {
                title: "Срок проведения OWD",
                date: submitState.reviewDate,
              },
              header: "Supply Portal -> Мои Документы | Проведение OWD",
            };

            const emailBody = createEmailBodyByTemplate(tempEmailBodyObject);

            const { items } = await getItemsRequest({
              listName: paramsListName,
              filters: { Key: "owd" },
            });

            const stringWithoutInvisibleChar = removeInvisibleChar(items[0].Value);
            const emailRequestBody: Partial<
              ListFieldsRecordType[typeof emailQueueListName]
            > = {
              Body: emailBody,
              DateToBeSentAt: new Date(),
              Title: "OWD",
              Subject: "Мои Документы | Проведение OWD",
              NotificationID: notificationID,
              Recepients: emp.user.email.toLowerCase(),
              RepeatAt: 0,
              RepeatTimes: 0,
            };

            const emailPromise = sendEmail(emailRequestBody).then(async () => {
              if (items[0] && items[0].Value) {
                if (
                  !moment(submitState.reviewDate).isSame(
                    emailRequestBody.DateToBeSentAt,
                    "day",
                  )
                ) {
                  const expiredEmailBody = createEmailBodyByTemplate({
                    ...tempEmailBodyObject,
                    periodDate: {
                      ...tempEmailBodyObject.periodDate,
                      expired: true,
                    },
                  });

                  emailRequestBody.Body = expiredEmailBody;
                  emailRequestBody.DateToBeSentAt = submitState.reviewDate;
                  emailRequestBody.RepeatAt = +stringWithoutInvisibleChar;
                  emailRequestBody.RepeatTimes = 100000;

                  await sendEmail(emailRequestBody);
                }
              }
            });

            emailPromises.push(emailPromise);
          }
        }

        await Promise.all(emailPromises);
      }

      const firstDoc = [...submitState.documents.values()][0];

      addAnalysis(
        currentUser,
        firstDoc.id,
        firstDoc.code.code,
        "isActive",
        "OWD",
      );

      setOpenSubmitNotificationModal(true);
      changeActive("send");

      dispatch(
        getNotificationsInfo({
          activeNotifyList: "send",
          listName: notificationsListName,
        }),
      );
    } catch (error) {
      console.log("error => handleOWDSubmit", error);
      throw new Error("Function not implemented.");
    } finally {
      changeStatus(Date.now());
      setSubmitLoading(false);
    }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        let dateString = "";

        if (typeOfNotify === "audit") {
          dateString = await getParamsByKey("auditDateDefault");
        } else if (typeOfNotify === "owd") {
          dateString = await getParamsByKey("owdDateDefault");
        }

        if (dateString && +dateString) {
          const reviewDateWithAddDays = moment().add(+dateString, "day").toDate();

          setSubmitState((prev) => ({
            ...prev,
            reviewDate: reviewDateWithAddDays,
          }));
        }
      } catch (error) {
        console.error("error", error);
      }
    }

    fetchData();
  }, [openDrawerAudit, openDrawerOWD, typeOfNotify]);

  return (
    <>
      {isIconOnly ? (
        <button onClick={() => setOpen(!open)}>
          <NotificationIcon />
        </button>
      ) : (
        <TGButton
          icon={<NotificationSmallIcon />}
          onClick={() => setOpen(!open)}
          color="bluedark"
        >
          {title}
        </TGButton>
      )}

      <Modal
        open={open}
        zIndex={zIndex}
        toggleDialog={closeDialog}
        modalClassName="!min-h-px !w-[calc(100vw-24px)] !min-w-0 !py-0 !rounded-2xl"
      >
        <FlexCol className="shrink">
          <header className="px-5 pt-6 pb-2">
            <h2>Отправка уведомления</h2>
          </header>

          <ChooseTypeOfNotifyMobile
            type={typeOfNotify}
            onChange={setTypeOfNotify}
          />

          <footer className="flex flex-col gap-3 px-5 py-4 border-t">
            <TGButton
              onClick={closeDialog}
              className="h-[46px] w-full justify-center text-[14px]"
            >
              Отмена
            </TGButton>
            <TGButton
              onClick={() =>
                typeOfNotify === "audit"
                  ? setOpenDrawerAudit(true)
                  : setOpenDrawerOWD(true)
              }
              color="bluedark"
              className="h-[46px] w-full justify-center text-[14px]"
            >
              Продолжить
            </TGButton>
          </footer>
        </FlexCol>

        {typeOfNotify === "audit" ? (
          <DrawerComponent
            zIndex={zIndex}
            open={openDrawerAudit}
            toggleDrawer={() => setOpenDrawerAudit((prev) => !prev)}
          >
            <header className="flex px-5 pt-5 pb-4 border-b shrink-0">
              <h1 className="text-black">Распространение ознакомления</h1>
            </header>
            <FlexCol className="divide-y grow">
              <SelectDocumentsMobile
                zIndex={zIndex}
                initial={submitState.documents}
                isMobileView={true}
                read={read?.documents}
                onResult={(items: Map<number, NotifyMyDocumentsType>) => {
                  setSubmitState((prev) => ({ ...prev, documents: items }));
                }}
              />
              <SelectEmployeesMobile
                zIndex={zIndex}
                title="Сотрудники"
                initial={submitState.employees}
                isMobileView={true}
                onResult={(items: Map<number, NotifyMyEmployeesType>) => {
                  setSubmitState((prev) => ({ ...prev, employees: items }));
                }}
              />
              <SelectTestsMobile
                zIndex={zIndex}
                onResult={(data: ITesting) => {
                  setSubmitState((prev) => ({ ...prev, test: data }));
                }}
              />
              <SelectReviewDateMobile
                zIndex={zIndex}
                title="Срок ознакомления"
                modalTitle="Укажите срок ознакомления"
                modalDescription="Срок ознакомления"
                initial={submitState.reviewDate}
                onResult={(date: Date) => {
                  setSubmitState((prev) => ({ ...prev, reviewDate: date }));
                }}
              />
              <SelectCommentMobile
                zIndex={zIndex}
                initial={submitState.comment}
                onResult={(text: string) => {
                  setSubmitState((prev) => ({ ...prev, comment: text }));
                }}
              />
            </FlexCol>
            <footer className="flex flex-col gap-3 p-5 border-t shrink-0">
              <TGButton
                disabled={!isButtonDisabledMemoized || submitLoading}
                onClick={handleAuditSubmit}
                color="bluedark"
                className={mobileDrawerFooterButtonClassName}
              >
                Отправить
              </TGButton>
              <TGButton
                disabled={submitLoading}
                onClick={() => {
                  setOpenDrawerAudit(false);
                  clearSubmit();
                }}
                className={mobileDrawerFooterButtonClassName}
              >
                Отмена
              </TGButton>
            </footer>
          </DrawerComponent>
        ) : (
          <DrawerComponent
            zIndex={zIndex}
            open={openDrawerOWD}
            toggleDrawer={() => setOpenDrawerOWD((prev) => !prev)}
          >
            <header className="flex px-5 pt-5 pb-4 border-b shrink-0">
              <h1 className="text-black">Планирование OWD</h1>
            </header>
            <FlexCol className="divide-y grow">
              <SelectDocumentsMobile
                zIndex={zIndex}
                initial={submitState.documents}
                isMobileView={true}
                read={read?.documents}
                onResult={(items: Map<number, NotifyMyDocumentsType>) => {
                  setSubmitState((prev) => ({ ...prev, documents: items }));
                }}
              />
              <SelectEmployeesMobile
                zIndex={zIndex}
                title="Кто проводит OWD"
                initial={submitState.employees}
                isMobileView={true}
                onResult={(items: Map<number, NotifyMyEmployeesType>) => {
                  setSubmitState((prev) => ({ ...prev, employees: items }));
                }}
              />
              <SelectReviewDateMobile
                zIndex={zIndex}
                title="Срок проведения OWD"
                modalTitle="Укажите срок проведения OWD"
                modalDescription="Срок проведения OWD"
                initial={submitState.reviewDate}
                onResult={(date: Date) => {
                  setSubmitState((prev) => ({ ...prev, reviewDate: date }));
                }}
              />
              <SelectCommentMobile
                zIndex={zIndex}
                initial={submitState.comment}
                onResult={(text: string) => {
                  setSubmitState((prev) => ({ ...prev, comment: text }));
                }}
              />
            </FlexCol>
            <footer className="flex flex-col gap-3 p-5 border-t shrink-0">
              <TGButton
                disabled={!isButtonDisabledMemoized || submitLoading}
                onClick={handleOWDSubmit}
                color="bluedark"
                className={mobileDrawerFooterButtonClassName}
              >
                Отправить
              </TGButton>
              <TGButton
                disabled={submitLoading}
                onClick={() => {
                  setOpenDrawerOWD(false);
                  clearSubmit();
                }}
                className={mobileDrawerFooterButtonClassName}
              >
                Отмена
              </TGButton>
            </footer>
          </DrawerComponent>
        )}
      </Modal>

      <Modal
        zIndex={zIndex ? zIndex + 10 : 70}
        modalClassName="!min-h-px !w-[calc(100vw-24px)] !min-w-0"
        open={openSubmitNotificationModal}
        toggleDialog={setOpenSubmitNotificationModal}
      >
        <header className="px-5 pb-2">
          <h2>Отправка уведомления</h2>
        </header>
        <div className="px-5 py-4">
          <p>Уведомления были успешно отправлены.</p>
        </div>
        <footer className="flex px-5 py-4 border-t">
          <TGButton
            color="bluedark"
            onClick={closeAllModals}
            className={mobileDrawerFooterButtonClassName}
          >
            Закрыть
          </TGButton>
        </footer>
      </Modal>

      <Modal
        zIndex={100}
        modalClassName="!min-h-0 !w-[calc(100vw-24px)] !min-w-0 text-center"
        open={submitLoading}
        toggleDialog={() => false}
        hideXMarkIcon={true}
      >
        <header className="px-5 pb-2">
          <h2>Идет сохранение данных</h2>
        </header>
        <div className="justify-center items-center px-5 py-4 space-y-5">
          <p className="text-base">Пожалуйста подождите...</p>
          <Loading />
        </div>
      </Modal>
    </>
  );
}
